'use client';
import type { InputPropTypes } from './types';

import React from 'react';
import { type FieldValues, useWatch } from 'react-hook-form';
import { useDisclosure } from '@chakra-ui/react';

import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  IconButton,
  Input as CkInput,
  InputGroup,
  InputLeftElement,
  PasswordStrength,
  Stack,
  Tooltip,
} from '@/components';
import { useTranslation } from '@/hooks';
import EyeOff from '@/svgs/EyeOff.svg';
import EyeOn from '@/svgs/EyeOn.svg';
import Hint from '@/svgs/hint.svg';

const PasswordInput = React.forwardRef<HTMLInputElement, InputPropTypes>(
  (
    {
      error,
      label,
      isSearch,
      isPassword,
      onClear,
      value,
      isRequired = false,
      hasHint = false,
      ...rest
    },
    forwardedRef
  ) => {
    const [show, setShow] = React.useState(false);
    const ref = React.useRef<HTMLInputElement>(null);

    const inputValue = useWatch(rest as FieldValues);
    const { t } = useTranslation('components.passwordInput');

    const { isOpen, onOpen, onToggle, onClose } = useDisclosure();

    return (
      <FormControl isInvalid={Boolean(error)} isRequired={isRequired}>
        {label && (
          <FormLabel display="flex" alignItems="center" gap={2}>
            {label}
            {hasHint && (
              <Tooltip
                isOpen={isOpen}
                label={t('strongPassword')}
                placement="top"
                hasArrow
                borderRadius={2}
                bgColor="secondary.500"
              >
                <Stack
                  onMouseEnter={onOpen}
                  onMouseLeave={onClose}
                  onClick={onToggle}
                >
                  <Icon as={Hint} width={4} height={4} />
                </Stack>
              </Tooltip>
            )}
          </FormLabel>
        )}
        <InputGroup ref={ref}>
          <CkInput
            ref={forwardedRef}
            type={show ? 'text' : 'password'}
            value={value}
            textAlign="left"
            {...rest}
            isLeftToRight
          />
          <InputLeftElement h="100%" onClick={() => setShow(!show)}>
            <IconButton
              onClick={() => setShow(!show)}
              aria-label="show password"
              variant="ghost"
              colorScheme="gray"
              icon={
                show ? (
                  <Icon boxSize="6" stroke="gray.700" as={EyeOn} />
                ) : (
                  <Icon boxSize="6" as={EyeOff} />
                )
              }
            />
          </InputLeftElement>
        </InputGroup>
        {hasHint && (
          <PasswordStrength
            password={inputValue as unknown as string}
            {...rest}
          />
        )}
        <FormErrorMessage message={error || ''} />
      </FormControl>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';
export default PasswordInput;
